import { GetListParams, GetOneParams } from 'react-admin';
import { CustomDataProvider, registerModuleDataProvider } from '../../providers/CustomDataProvider';
import { Resources } from '../resource';
import {
  createNotificationTemplate,
  deleteNotificationTemplate,
  listNotificationTemplates,
  updateNotificationTemplate,
} from '../../services/notificationConfigsService';

const TEMPLATES = Resources.notificationConfigsTemplates;

const recordCache = new Map<string, any>();

const templatesDataProvider: CustomDataProvider = {
  async getList(_resource: string, params: GetListParams): Promise<any> {
    const { page = 1, perPage = 20 } = params.pagination || {};
    const filter = params.filter ?? {};

    const response = await listNotificationTemplates({
      templateCode: filter.templateCode || undefined,
      languageCode: filter.languageCode || undefined,
      targetAudience: filter.targetAudience || undefined,
      isActive:
        filter.isActive !== undefined && filter.isActive !== '' ? filter.isActive : undefined,
      platforms: filter.platforms?.length ? filter.platforms : undefined,
      limit: perPage,
      offset: (page - 1) * perPage,
    });

    const { items = [], totalCount = 0 } = response.data || {};
    // A template is identified by templateCode + channelType and holds its per-language translations.
    const records = items.map((item) => ({
      ...item,
      id: item.id ?? `${item.templateCode}:${item.channelType}`,
      translations: item.translations ?? [],
    }));
    records.forEach((r) => recordCache.set(String(r.id), r));
    return { data: records, total: totalCount };
  },

  async getOne(_resource: string, params: GetOneParams): Promise<any> {
    const cached = recordCache.get(String(params.id));
    if (cached) return { data: cached };
    return { data: { id: params.id } };
  },

  async create(_resource: string, params: any): Promise<any> {
    const response = await createNotificationTemplate(params.data);
    const payload = response.data ?? params.data;
    return { data: { id: payload?.id ?? `${Date.now()}`, ...payload } };
  },

  async update(_resource: string, params: any): Promise<any> {
    // templateCode + channelType identify the template, so fall back to the previous record
    // if the form omitted either field.
    const data = {
      ...params.data,
      templateCode: params.data?.templateCode ?? params.previousData?.templateCode,
      channelType: params.data?.channelType ?? params.previousData?.channelType,
    };
    await updateNotificationTemplate(data);
    return { data: { id: params.id, ...data } };
  },

  // The API deletes one translation at a time, so removing a template removes each of its
  // translations.
  async delete(_resource: string, params: any): Promise<any> {
    const prev = params.previousData ?? {};
    const translations: any[] = prev.translations ?? [];
    await Promise.all(
      translations.map((tr) =>
        deleteNotificationTemplate(prev.templateCode, tr.languageCode, prev.channelType)
      )
    );
    return { data: { id: params.id } };
  },
};

registerModuleDataProvider(TEMPLATES, templatesDataProvider);
